<script lang="ts" setup>
import { ref, watch } from 'vue';

import { isSameMonth } from '@/utilities/dateutils';
import {
    ButtonComp,
    DateComp,
    InputDateTimeCalendarDaySelector,
    InputDateTimeCalendarMonthsSelector,
    InputDateTimeCalendarYearSelector,
} from '@/components';

const props = defineProps<{
    monthToDisplay: Date;
}>();

const emits = defineEmits(['dateSelected']);

const currentMode = ref<'DAYS' | 'MONTHS' | 'YEARS'>('DAYS');

// The month currently displayed in the datepicker. We only care about year and month here
const displayMonth = ref(new Date(props.monthToDisplay));

watch(
    () => props.monthToDisplay,
    (newVal, oldVal) => {
        if (isSameMonth(oldVal, newVal)) {
            return;
        }
        displayMonth.value = new Date(newVal);
    },
);

// Revert to days whenever the displayMonth changes (After choosing year, month or editing the mask input)
watch(displayMonth, () => setMode('DAYS'));

const changeMonth = (delta: number) => {
    const d = new Date(displayMonth.value);
    d.setMonth(d.getMonth() + delta);
    displayMonth.value = d;
};

const daySelected = (selectedDate: Date) => {
    emits('dateSelected', selectedDate);
};

const setMode = (mode: 'DAYS' | 'MONTHS' | 'YEARS') => {
    currentMode.value = mode;
};
</script>

<template>
    <div class="d-inline-flex flex-column border shadow p-2 inputdatetimecalendarwrapper bg-body">
        <!-- Years -->
        <InputDateTimeCalendarYearSelector
            v-if="currentMode == 'YEARS'"
            v-bind:model-value="displayMonth"
            v-on:update:model-value="(selectedYear) => (displayMonth = selectedYear)"
        />
        <!-- Months -->
        <div
            class="dateinput-grid grid-months"
            v-if="currentMode == 'MONTHS'"
        >
            <InputDateTimeCalendarMonthsSelector
                v-if="currentMode == 'MONTHS'"
                v-bind:model-value="displayMonth"
                v-on:update:model-value="(selectedMonth) => (displayMonth = selectedMonth)"
            />
        </div>
        <!-- Days -->
        <div v-if="currentMode == 'DAYS'">
            <!-- This is the Month Year header of the calendar -->
            <div class="d-flex">
                <ButtonComp
                    button-type="Secondary"
                    icon="fa-arrow-left"
                    @click="changeMonth(-1)"
                />
                <span class="px-2 flex-grow-1 align-self-center text-center">
                    <span
                        class="pe-1 text-capitalize"
                        @click="setMode('MONTHS')"
                    >
                        <DateComp
                            :value="displayMonth"
                            format="MONTHLONG"
                        />
                    </span>
                    <span @click="setMode('YEARS')">
                        <DateComp
                            :value="displayMonth"
                            format="YEARONLY"
                        />
                    </span>
                </span>
                <ButtonComp
                    button-type="Secondary"
                    icon="fa-arrow-right"
                    @click="changeMonth(1)"
                />
            </div>
            <InputDateTimeCalendarDaySelector
                :displayMonth="displayMonth"
                v-bind:parent-bound="monthToDisplay"
                v-on:update:parent-bound="daySelected"
            />
        </div>
    </div>
</template>
